import axios from "axios";
import { uploadDocument } from "../../../api/elearning/documents/documents";

let subscribers = new Set();
let jobsById = new Map();

const notify = () => {
  const snapshot = Array.from(jobsById.values());
  for (const cb of subscribers) cb(snapshot);
};

export const formatBytesPerSecond = (bps) => {
  if (!Number.isFinite(bps) || bps <= 0) return "0 B/s";
  if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(2)} MB/s`;
  if (bps >= 1024) return `${(bps / 1024).toFixed(2)} KB/s`;
  return `${Math.round(bps)} B/s`;
};

export const formatEtaSeconds = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return "";
  const s = Math.ceil(sec);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  if (mm > 0) return `~${mm}p ${String(ss).padStart(2, "0")}s`;
  return `~${ss}s`;
};

export const subscribeDocumentUploads = (cb) => {
  subscribers.add(cb);
  cb(Array.from(jobsById.values()));
  return () => {
    subscribers.delete(cb);
  };
};

export const clearDocumentUploads = () => {
  for (const job of jobsById.values()) {
    if (job.status === "uploading" || job.status === "queued") {
      if (job.controller) {
        try {
          job.controller.abort();
        } catch (_) {}
      }
    }
  }
  jobsById.clear();
  notify();
};

export const removeDocumentUploadJob = (jobId) => {
  const job = jobsById.get(jobId);
  if (!job) return;
  if (job.status === "uploading" || job.status === "queued") {
    if (job.controller) {
      try {
        job.controller.abort();
      } catch (_) {}
    }
  }
  jobsById.delete(jobId);
  notify();
};

export const cancelDocumentUpload = (jobId) => {
  const job = jobsById.get(jobId);
  if (!job) return;
  if (job.controller) {
    try {
      job.controller.abort();
    } catch (_) {}
  }
  updateJob(jobId, {
    status: "error",
    phase: "error",
    errorMessage: "Đã hủy tải lên",
  });
};

const getCryptoId = () => {
  const hasRandomUUID =
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function";
  return hasRandomUUID
    ? window.crypto.randomUUID()
    : `job_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const updateJob = (jobId, patch) => {
  const cur = jobsById.get(jobId);
  if (!cur) return;
  jobsById.set(jobId, { ...cur, ...patch });
  notify();
};

// files: Array<{file, size, type, ext, baseName}>
// meta: { subject, courseId, docType, description, status }
export const startDocumentUploads = ({ files, meta }) => {
  const selected = Array.isArray(files) ? files.slice(0, 10) : [];
  if (!selected.length) return;

  const jobs = selected.map((f) => {
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    return {
      id: getCryptoId(),
      fileName: f.baseName || f.file?.name || "Tài liệu",
      size: f.size || f.file?.size || 0,
      ext: f.ext || "",
      docType: meta?.docType || "",
      percent: 0,
      phase: "upload",
      speedText: "",
      etaText: "",
      status: "queued",
      errorMessage: null,
      controller,
    };
  });

  for (const j of jobs) jobsById.set(j.id, j);
  notify();

  const startedAt = new Map();
  const lastSample = new Map();
  const lastUiUpdate = new Map();
  const percentRef = new Map();

  for (const j of jobs) {
    startedAt.set(j.id, Date.now());
    lastSample.set(j.id, { loaded: 0, t: Date.now() });
    lastUiUpdate.set(j.id, 0);
    percentRef.set(j.id, 0);
  }

  let index = 0;
  // Cho phép tối đa 3 uploads đồng thời để tránh nghẽn băng thông, còn lại queued
  const concurrency = Math.min(3, jobs.length);

  const workers = Array.from({ length: concurrency }, () =>
    (async () => {
      while (true) {
        const i = index++;
        if (i >= jobs.length) break;

        const job = jobs[i];
        const fileItem = selected[i];

        // Nếu job đã bị hủy trước khi bắt đầu
        const currentJob = jobsById.get(job.id);
        if (!currentJob || currentJob.status === "error") continue;

        updateJob(job.id, { status: "uploading", phase: "upload" });

        try {
          const data = new FormData();
          data.append("file", fileItem.file);
          data.append("file_name", fileItem.baseName || "Document");
          data.append(
            "file_type",
            fileItem.type || fileItem.file?.type || "application/octet-stream"
          );
          data.append("file_extension", fileItem.ext || "");
          data.append(
            "file_size",
            String(fileItem.size || fileItem.file?.size || 0)
          );
          data.append("course_id", meta.courseId || "");
          data.append("type", meta.docType);
          data.append("subject", meta.subject);
          data.append("description", meta.description || "");
          data.append("is_active", meta.status === "1" ? "true" : "false");

          await uploadDocument(data, {
            signal: job.controller?.signal,
            onProgress: ({ loaded, total }) => {
              const now = Date.now();
              const startedAtMs = startedAt.get(job.id) ?? now;

              const prev = lastSample.get(job.id) ?? { loaded: 0, t: now };
              const dt = now - prev.t;
              const dLoaded = loaded - prev.loaded;
              const hasTotal = total && total > 0;

              const rawPercent = hasTotal
                ? Math.min(100, Math.round((loaded / total) * 100))
                : percentRef.get(job.id) ?? 0;

              percentRef.set(job.id, rawPercent);

              let bps = 0;
              if (dt > 0 && dLoaded > 0) bps = (dLoaded / dt) * 1000;

              const speedText = bps ? formatBytesPerSecond(bps) : "";
              const remainingSec =
                hasTotal && bps > 0 && rawPercent < 100
                  ? (total - loaded) / bps
                  : null;
              const etaText = remainingSec ? formatEtaSeconds(remainingSec) : "";

              const nextPhase = rawPercent >= 100 ? "server" : "upload";
              const displayPercent =
                nextPhase === "server" ? Math.min(99, rawPercent) : rawPercent;

              const nextElapsedMs = now - startedAtMs;
              lastSample.set(job.id, { loaded, t: now });

              if (
                now - (lastUiUpdate.get(job.id) ?? 0) < 200 &&
                rawPercent !== 100
              )
                return;
              lastUiUpdate.set(job.id, now);

              updateJob(job.id, {
                percent: displayPercent,
                phase: nextPhase,
                speedText,
                etaText: nextPhase === "upload" ? etaText : "",
                elapsedMs: nextElapsedMs,
              });
            },
          });

          updateJob(job.id, { status: "done", phase: "done", percent: 100 });

          // Bắn event để màn hình danh sách tài liệu tự động reload hiển thị file mới
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("ima:document_upload_success", {
                detail: { jobId: job.id, fileName: job.fileName },
              })
            );
          }
        } catch (err) {
          if (
            axios.isCancel?.(err) ||
            err?.name === "CanceledError" ||
            err?.message === "canceled"
          ) {
            updateJob(job.id, {
              status: "error",
              phase: "error",
              errorMessage: "Đã hủy tải lên",
              percent: 0,
            });
          } else {
            const msg =
              err?.response?.data?.message ||
              err?.message ||
              "Không thể tải lên tài liệu.";
            updateJob(job.id, {
              status: "error",
              phase: "error",
              errorMessage: msg,
              percent: 0,
            });
          }
        }
      }
    })()
  );

  // Giữ job hoàn tất trong danh sách 30 phút rồi dọn dẹp
  void Promise.all(workers).then(() => {
    const finalIds = jobs.map((j) => j.id);
    setTimeout(() => {
      for (const id of finalIds) jobsById.delete(id);
      notify();
    }, 30 * 60_000);
  });
};
